package fr.frux.feature.treedetail.identity

import fr.frux.core.common.error.AppError
import fr.frux.feature.treedetail.TreeDetail
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.TreeCorrection
import fr.frux.shared.domain.model.TreeIdentity
import fr.frux.shared.domain.model.changes
import fr.frux.shared.domain.model.Species as ReferentialSpecies

/** Champ d'identité touché par une correction, pour le message et la mise en évidence. */
enum class IdentityField { SPECIES, POSITION, NOTE }

sealed interface HistoryLoad {
    data object Loading : HistoryLoad

    data class Loaded(
        val rows: List<HistoryRow>,
        val truncated: Boolean,
    ) : HistoryLoad

    data class Failed(
        val error: AppError,
    ) : HistoryLoad
}

/**
 * Feuille ouverte sur le bloc Identité. Les brouillons vivent ici, dans l'état du ViewModel, et
 * non dans un `remember` d'écran que Nav3 détruirait.
 */
sealed interface IdentitySheet {
    data class Species(
        val query: String,
        val results: List<ReferentialSpecies>,
        val species: ReferentialSpecies?,
        val certainty: Certainty,
        val reason: String,
        val saving: Boolean,
        val error: AppError?,
        val canSave: Boolean,
    ) : IdentitySheet

    data class Note(
        val text: String,
        val reason: String,
        val saving: Boolean,
        val error: AppError?,
        val canSave: Boolean,
    ) : IdentitySheet

    data class History(
        val load: HistoryLoad,
    ) : IdentitySheet
}

/** Retour d'une correction, affiché en snackbar puis consommé. */
sealed interface CorrectionMessage {
    /** [undo] est la correction inverse qu'envoie « Annuler ». */
    data class Saved(
        val field: IdentityField,
        val undo: TreeCorrection,
    ) : CorrectionMessage

    data object Undone : CorrectionMessage

    data class UndoFailed(
        val error: AppError,
    ) : CorrectionMessage
}

data class IdentityUi(
    val sheet: IdentitySheet? = null,
    val message: CorrectionMessage? = null,
    val highlighted: IdentityField? = null,
    /** Vrai dès qu'une correction de cette fiche a été acceptée pendant la visite. */
    val correctedByMe: Boolean = false,
)

internal fun TreeDetail.identity(): TreeIdentity =
    TreeIdentity(
        speciesId = speciesId,
        certainty = certainty,
        latitude = latitude,
        longitude = longitude,
        description = description,
    )

internal val TreeCorrection.field: IdentityField
    get() =
        when (this) {
            is TreeCorrection.Species -> IdentityField.SPECIES
            is TreeCorrection.Position -> IdentityField.POSITION
            is TreeCorrection.Note -> IdentityField.NOTE
        }

/** Motif facultatif : un motif vide part nul. */
private fun String.asReason(): String? = trim().ifEmpty { null }

/** Nul tant qu'aucune espèce n'est choisie : rien à envoyer. */
internal fun IdentitySheet.Species.toCorrection(): TreeCorrection.Species? =
    species?.let { TreeCorrection.Species(it.id, certainty, reason.asReason()) }

internal fun IdentitySheet.Note.toCorrection(): TreeCorrection.Note = TreeCorrection.Note(text, reason.asReason())

/** Recalcule `canSave` : une correction qui ne changerait rien ne s'envoie pas. */
internal fun IdentitySheet.refreshed(identity: TreeIdentity?): IdentitySheet =
    when (this) {
        is IdentitySheet.Species -> {
            copy(canSave = !saving && identity != null && toCorrection()?.changes(identity) == true)
        }

        is IdentitySheet.Note -> {
            copy(canSave = !saving && identity != null && toCorrection().changes(identity))
        }

        is IdentitySheet.History -> {
            this
        }
    }
